import ResultsFormatterBase from "../../formatters/ResultsFormatterBase.js";
import PitchReprocessed from "../../enums/PitchReprocessed.js";
import BaseballResultsProcessor from '../../entities/BaseballResultsProcessor.js';
import { DataSection, PitchData, ResultData, ResultDataNew, ResultType } from "../../enums/index.js";

class PitchCountFormatter extends ResultsFormatterBase {
    /**
     * PitchCountFormatter constructor
     * @param {undefined|boolean} tryToCalculate - default true, when set to false only scoring data will be used to determine pitch count (no recalculation)
     */
    constructor(tryToCalculate, newStructure) {
        super();

        if (typeof(tryToCalculate) === 'undefined') {
            this.tryToCalculate = true;
        }
        else if(typeof(tryToCalculate) === 'boolean') {
            this.tryToCalculate = tryToCalculate;
        }
        else {
            throw new TypeError('tryToCalculate must be boolean or undefined');
        }

        if (typeof(newStructure) === 'undefined') {
            this.newStructure = false;
        }
        else if(typeof(newStructure) === 'boolean') {
            this.newStructure = newStructure;
        }
        else {
            throw new TypeError('newStructure must be boolean or undefined');
        }
    }

    formatNewStructure(results) {
        const dataKey = ResultData.mapPath(ResultData.Data.key);
        const typeKey = ResultData.mapPath(ResultData.Type.key);

        let pitchCountPerPitcher = {};
        for (let i = 0; i < results.length; i++) {
            let result = results[i];

            if (result || !result.hasOwnProperty(dataKey) || !result[dataKey]) {
                continue;
            }

            if (!ResultType.hasPitch(result[typeKey])) {
                continue;
            }

            if (
                !result[dataKey].hasOwnProperty(DataSection.Scoring.key)
                || result[dataKey][DataSection.Scoring.key] === null
            ) {
                result[dataKey][DataSection.Scoring.key] = {};
            }

            let uniquePlayerId = this.getUniquePlayerTagId(result);

            if (!uniquePlayerId) {
                // TODO - maybe throw ? or set 0
                result[dataKey][DataSection.Scoring.key][PitchReprocessed.PitchCountR.key] = null;
                continue;
            }

            if (
                result[dataKey][DataSection.Scoring.key].hasOwnProperty(PitchData.AfterPlayPitchCount.key)
                && !isNaN(result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPitchCount.key])
                && result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPitchCount.key] > 0
            ) {
                // prefer scoring data
                result[dataKey][DataSection.Scoring.key][PitchReprocessed.PitchCountR.key] = result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPitchCount.key];
                pitchCountPerPitcher[uniquePlayerId] = result[dataKey][DataSection.Scoring.key][PitchData.AfterPlayPitchCount.key];

                continue;
            }
            else if (!this.tryToCalculate) {
                result[dataKey][DataSection.Scoring.key][PitchReprocessed.PitchCountR.key] = null;
                continue;
            }

            if (!pitchCountPerPitcher.hasOwnProperty(uniquePlayerId)) {
                pitchCountPerPitcher[uniquePlayerId] = 0;
            }

            result[dataKey][DataSection.Scoring.key][PitchReprocessed.PitchCountR.key] = ++pitchCountPerPitcher[uniquePlayerId];
        }

        return results;
    }

    format(results) {
        if (this.newStructure) {
            return this.formatNewStructure(results);
        }

        let pitchCountPerPitcher = {};
        for (let i = 0; i < results.length; i++) {
            let result = results[i];

            if (!result || !result.hasOwnProperty(ResultData.Data.key) || !result[ResultData.Data.key]) {
                continue;
            }

            if (result[ResultData.Type.key] === ResultType.Hit.key) {
                // check if there is related pitch...
                let relatedPitch = BaseballResultsProcessor.getRelatedResult(result[ResultData.PitchId.key], results);
                if (relatedPitch){
                    result[ResultData.Data.key][PitchReprocessed.PitchCountR.key] = relatedPitch[ResultData.Data.key][PitchReprocessed.PitchCountR.key];
                }
                else{
                    result[ResultData.Data.key][PitchReprocessed.PitchCountR.key] = null;
                }

                continue;
            }
            else if (result[ResultData.Type.key] !== ResultType.Pitch.key) {
                continue;
            }

            let uniquePlayerId = this.getUniquePlayerTagId(result);

            if (!uniquePlayerId) {
                // TODO - maybe throw ? or set 0
                result[ResultData.Data.key][PitchReprocessed.PitchCountR.key] = null;
                continue;
            }

            if (
                result[ResultData.Data.key].hasOwnProperty(PitchData.AfterPlayPitchCount.key)
                && !isNaN(result[ResultData.Data.key][PitchData.AfterPlayPitchCount.key])
                && result[ResultData.Data.key][PitchData.AfterPlayPitchCount.key] > 0
            ) {
                // prefer scoring data
                result[ResultData.Data.key][PitchReprocessed.PitchCountR.key] = result[ResultData.Data.key][PitchData.AfterPlayPitchCount.key];
                pitchCountPerPitcher[uniquePlayerId] = result[ResultData.Data.key][PitchData.AfterPlayPitchCount.key];

                continue;
            }
            else if (!this.tryToCalculate) {
                result[ResultData.Data.key][PitchReprocessed.PitchCountR.key] = null;
                continue;
            }

            if (!pitchCountPerPitcher.hasOwnProperty(uniquePlayerId)) {
                pitchCountPerPitcher[uniquePlayerId] = 0;
            }

            result[ResultData.Data.key][PitchReprocessed.PitchCountR.key] = ++pitchCountPerPitcher[uniquePlayerId];
        }

        return results;
    }

    getUniquePlayerTagId(result) {
        const sessionIdProp = this.newStructure ? ResultDataNew.SessionId.key : ResultData.SessionId.key;
        const tagIdProp = this.newStructure ? ResultDataNew.Pitcher.key : ResultData.TagId.key;

        let uniquePlayerId = result.hasOwnProperty(sessionIdProp) ? result[sessionIdProp] + '-': '';

        if (result.hasOwnProperty(tagIdProp) && result[tagIdProp] != 0) {
            uniquePlayerId += this.getTagId(result[tagIdProp]);
        }
        else if (result.hasOwnProperty(ResultData.PlayerId.key) && result[ResultData.PlayerId.key] != 0) {
            uniquePlayerId += this.getPlayerId(result[ResultData.PlayerId.key]);
        }
        else {
            return null;
        }

        return uniquePlayerId;
    }

    getTagId(id) {
        return `tag-${id}`;
    }

    getPlayerId(id) {
        return `player-${id}`;
    }
};

export default PitchCountFormatter;